<template>
    <div class="product-card-container">
        <div v-if="product" ref="product-card" class="product-card" data-cy="product-card" @click="productClickHandler">
            <div
                class="cover-image"
                :style="{
                    'background-image': 'url(' + product.image_url + ')',
                }"
            >
                <product-tags :tags="product.tags"></product-tags>
            </div>
            <div class="details">
                <div class="title">
                    <span>{{ product.name }}</span>
                </div>
                <div class="price">
                    <span v-if="hasDiscount" class="discount">{{ salePrice }} </span>
                    <span :class="{ discounted: hasDiscount }" class="subtotal">{{ listPrice }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import productTags from '@/components/common/product-tags';
import { mapState } from 'vuex';
const IN_VIEW_PERCENTAGE = 90;

export default {
    name: 'ProductCard',
    components: {
        productTags,
    },
    props: {
        product: {
            type: Object,
            default: () => {},
        },
        position: {
            type: Number,
            default: 0,
        },
    },
    data() {
        return {
            impressionEventFired: false,
        };
    },
    computed: {
        ...mapState(['service', 'category']),
        hasDiscount() {
            return this.product.sale_price < this.product.list_price;
        },
        currency() {
            return this.product.currency;
        },
        salePrice() {
            return this.displayCurrency(this.product.sale_price);
        },
        listPrice() {
            return this.displayCurrency(this.product.list_price);
        },
    },
    mounted() {
        this.$nextTick(() => {
            window.addEventListener('resize', this.isInView);
            window.addEventListener('scroll', this.isInView, true);

            this.isInView();
        });
    },
    beforeDestroy() {
        this.removeEventListeners();
    },
    methods: {
        productClickHandler() {
            this.$router.push({ name: 'product', params: { productId: this.product.no, category: this.category.no } });
        },
        displayCurrency(amount) {
            return this.currency === 'NX'
                ? `${this.$n(amount)} ${this.currency}`
                : this.currency === 'TWD'
                ? `${this.$n(amount)} ${this.$t('shop.points')}`
                : `${this.$n(amount, 'currency', this.currency)}`;
        },
        removeEventListeners() {
            window.removeEventListener('scroll', this.isInView);
            window.removeEventListener('resize', this.isInView);
        },
        isInView() {
            if (this.impressionEventFired || !this.$refs['product-card']) return;
            const rect = this.$refs['product-card'].getBoundingClientRect(),
                windowHeight = window.innerHeight || document.documentElement.clientHeight,
                isInView = !(
                    Math.floor(100 - ((rect.top >= 0 ? 0 : rect.top) / +-(rect.height / 1)) * 100) <
                        IN_VIEW_PERCENTAGE ||
                    Math.floor(100 - ((rect.bottom - windowHeight) / rect.height) * 100) < IN_VIEW_PERCENTAGE
                );

            if (isInView) {
                this.impressionEventFired = true;
                this.removeEventListeners();
                dataLayer.push({
                    event: 'productImpression',
                    ecommerce: {
                        impressions: [
                            {
                                name: this.product.name,
                                id: this.product.no,
                                brand: this.service.service_name,
                                category: `${this.service.service_name}-${this.category.no}`,
                                list: 'Shop Item List',
                                position: this.position,
                            },
                        ],
                    },
                });
            }
        },
    },
};
</script>

<style lang="less" scoped>
.product-card-container {
    display: flex;
    flex: 0 0 25%;
    justify-content: center;
    max-width: 25%;
    padding: 10px;
    .product-card {
        background-color: #31333d;
        border-radius: 5px;
        cursor: pointer;
        overflow: hidden;
        position: relative;
        width: 100%;
        display: flex;
        flex-direction: column;
        &:hover {
            transform: scale(1.03);
        }
        .cover-image {
            background-position: top;
            background-repeat: no-repeat;
            background-size: cover;
            height: 348px;
            width: 100%;
            .tags-container {
                display: flex;
                flex-wrap: wrap;
                justify-content: flex-end;
                position: absolute;
                right: 0;
                top: 0;
                .tag {
                    align-items: center;
                    background-color: #ffa039;
                    color: rgba(0, 0, 0, 0.8);
                    display: flex;
                    font-size: 12px;
                    font-weight: bold;
                    height: 25px;
                    justify-content: center;
                    letter-spacing: -0.3px;
                    margin: 0 0 1px 1px;
                    min-width: 100px;
                    padding: 0 10px;
                }
            }
        }
        .details {
            display: flex;
            flex-direction: column;
            flex-grow: 1;
            justify-content: space-between;
            padding: 20px;
            .title {
                color: #d9d9d9;
                font-size: 16px;
                letter-spacing: -0.4px;
                line-height: 1.5;
                margin-bottom: 40px;
            }
            .price {
                display: flex;
                justify-content: flex-end;
                flex-wrap: wrap;
                .subtotal {
                    color: #fff;
                    font-size: 20px;
                    font-weight: bold;
                    margin: 10px 0 0 10px;
                    &.discounted {
                        color: #999;
                        font-size: 16px;
                        font-weight: normal;
                        text-decoration: line-through;
                    }
                }
                .discount {
                    color: #f2b600;
                    font-size: 20px;
                    font-weight: bold;
                    margin-top: 10px;
                }
            }
        }
    }
}
@media (max-width: 1024px) {
    .product-card-container {
        flex: 0 0 33.33%;
        max-width: 33.33%;
        .product-card {
            .cover-image {
                height: 350px;
            }
        }
    }
}
@media (max-width: 768px) {
    .product-card-container {
        flex: 0 0 50%;
        max-width: 50%;
        .product-card {
            .cover-image {
                height: 205px;
            }
            .details {
                padding: 15px;
                .title {
                    font-size: 13.5px;
                    letter-spacing: -0.34px;
                    line-height: 1.63;
                    margin-bottom: 30px;
                }
                .price {
                    .subtotal {
                        font-size: 18px;
                        &.discounted {
                            font-size: 14px;
                        }
                    }
                    .discount {
                        font-size: 18px;
                    }
                }
            }
        }
    }
}
@media (max-width: 480px) {
    .product-card-container {
        flex: 0 0 100%;
        max-width: 100%;
        padding: 10px 0;
    }
}
</style>
